/**
 * Aaron's decline referral (APIP-5250) — the rule for whether an `aarons`
 * payload can be put on screen, and the formatters for what's shown.
 *
 * The frontend carries no eligibility logic of its own: the decision to refer
 * is made server-side, and this module only decides whether the payload it
 * was handed is complete enough to render.
 *
 * See spec/aarons-decline-flow/SPEC.md §4.
 */

/**
 * Money on the wire may arrive as a JSON number or as a decimal string — LMS
 * serialises amounts both ways depending on the field. Normalise to a number
 * once, here, so nothing downstream has to guess. Returns null for anything
 * that is not a finite amount, which keeps "$NaN/mo" off the screen.
 */
export const toAmount = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

/**
 * True only for an absolute http(s) URL.
 *
 * `cta_url` is handed straight to `window.open`, and to `window.location.href`
 * when a popup is blocked. A `javascript:` or `data:` URL in either of those
 * is script execution, and both parse perfectly well as URLs — so the protocol
 * is checked explicitly. The payload comes from LMS rather than the browser,
 * which makes this defence in depth rather than a live hole, but a URL we
 * navigate the top-level window to is worth being sure about.
 *
 * Checked here rather than at the point of navigation on purpose: an approval
 * we cannot send the customer to safely is not renderable, so it falls through
 * to the standard decline by the same path as every other bad payload,
 * instead of putting a screen up with a button that refuses to work.
 */
export const isSafeHttpUrl = value => {
  if (typeof value !== 'string' || value.trim() === '') {
    return false;
  }

  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch (e) {
    // Not an absolute URL at all — a relative path or junk.
    return false;
  }
};

/**
 * True only for an approval we can render in full.
 *
 * `approved` is compared strictly to `true`: a serialiser that sends the
 * string "false" is truthy and must not turn a decline into an approval.
 *
 * `cta_url` and `approval_amount` are load-bearing — without them the screen
 * is a dead button or a blank number. `example_cash_price` is deliberately
 * not checked: losing the "up to $X in merchandise" line is not worth losing
 * the referral.
 */
export const isRenderableApproval = aarons => {
  if (!aarons || aarons.approved !== true) {
    return false;
  }

  return isSafeHttpUrl(aarons.cta_url) && toAmount(aarons.approval_amount) !== null;
};

const monthlyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const cashPriceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatMonthly = amount => monthlyFormatter.format(amount);

export const formatCashPrice = amount => cashPriceFormatter.format(amount);
